import React, { useMemo, type CSSProperties } from "react";

import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import type { FundDataWithType } from "@hsl/fund-explorer/metaSchemas";

import { useIsFCSEnabled } from "../IsFCSEnabledContext";

type ClickHandler = {
    (cookied: boolean): void;
};
export interface Props {
    fund: FundDataWithType;
    isWatching: boolean;
    isSubscribed: boolean;
    hasUpdates: boolean;
    onClickWatch?: ClickHandler;
    onClickSubscribe?: ClickHandler;
    className?: string;
    ignoreBasket?: boolean;
}

export const redDot = `
before:absolute
before:content-['']
before:top-0
before:rounded-full
before:right-0
before:w-2
before:h-2
before:bg-core-red
before:z-10`;

const SubscriptionButtons = ({
    fund,
    isWatching,
    isSubscribed,
    hasUpdates,
    onClickWatch,
    onClickSubscribe,
    className,
    ignoreBasket,
}: Props) => {
    const basket = useFCSubscriptionStore((state) => state.selectionBasket);
    const basketMode = useFCSubscriptionStore(
        (state) => state.selectionBasketMode,
    );

    const enabled = useIsFCSEnabled();
    if (!enabled) return null;

    const basketHasItems = basket.funds.length > 0;

    const tooManyItemsInBasket = basket.funds.length >= 10;

    const isInBasket = basket.funds.some((f) => f.data.id === fund.data.id);

    const watchDisabled =
        (basketHasItems && basketMode !== "watch") ||
        (basketHasItems && isWatching) ||
        (tooManyItemsInBasket && !isInBasket);
    const subscribeDisabled =
        (basketHasItems && basketMode !== "subscribe") ||
        (basketHasItems && isSubscribed) ||
        (tooManyItemsInBasket && !isInBasket);

    const watchSelected = isInBasket && basketMode === "watch";
    const watchActive = isWatching || watchSelected;

    const subscribeSelected = isInBasket && basketMode === "subscribe";
    const subscribeActive = isSubscribed || subscribeSelected;

    const watchJiggle = !watchDisabled && !isInBasket && basketHasItems;
    const subscribeJiggle = !subscribeDisabled && !isInBasket && basketHasItems;

    return (
        <div className={cx("relative inline-flex flex-row gap-1.5", className)}>
            {ignoreBasket ? (
                <SubscriptionButtonsInner
                    watchActive={isWatching}
                    subscribeActive={isSubscribed}
                    onClickWatch={onClickWatch}
                    onClickSubscribe={onClickSubscribe}
                    hasUpdates={hasUpdates}
                />
            ) : (
                <SubscriptionButtonsInner
                    watchDisabled={watchDisabled}
                    watchActive={watchActive}
                    watchJiggle={watchJiggle}
                    onClickWatch={onClickWatch}
                    subscribeDisabled={subscribeDisabled}
                    subscribeActive={subscribeActive}
                    subscribeJiggle={subscribeJiggle}
                    onClickSubscribe={onClickSubscribe}
                    hasUpdates={hasUpdates}
                />
            )}
        </div>
    );
};

const SubscriptionButtonsInner = ({
    watchDisabled,
    watchActive,
    watchJiggle,
    onClickWatch,
    subscribeDisabled,
    subscribeActive,
    subscribeJiggle,
    onClickSubscribe,
    hasUpdates,
}: {
    watchDisabled?: boolean;
    watchActive: boolean;
    watchJiggle?: boolean;
    onClickWatch?: ClickHandler;
    subscribeDisabled?: boolean;
    subscribeActive: boolean;
    subscribeJiggle?: boolean;
    onClickSubscribe?: ClickHandler;
    hasUpdates?: boolean;
}) => {
    const jiggleDelay = useMemo(() => Math.random().toFixed(2), []);
    const user = useFCSubscriptionStore((state) => state.user);
    const handleClick = (cb?: ClickHandler) => (evt: React.MouseEvent) => {
        evt.stopPropagation();
        cb && cb(Boolean(user));
    };
    return (
        <>
            <button
                className={cx(
                    "transition-colors",
                    watchDisabled && "cursor-default opacity-50",
                )}
                aria-disabled={watchDisabled}
                disabled={watchDisabled}
                aria-roledescription={`Watch fund (currently ${
                    watchActive ? "watching" : "not watching"
                })`}
                onClick={handleClick(onClickWatch)}
            >
                <div
                    style={
                        {
                            "--animate-jiggle-delay": `-${jiggleDelay}s`,
                        } as CSSProperties
                    }
                    className={cx(
                        "delay-[--animate-jiggle-delay]",
                        watchJiggle && "animate-jiggle",
                    )}
                >
                    <WatchIcon active={watchActive} />
                </div>
            </button>
            <button
                className={cx(
                    "transition-colors",
                    subscribeDisabled && "cursor-default opacity-50",
                    hasUpdates && "relative",
                )}
                aria-disabled={subscribeDisabled}
                disabled={subscribeDisabled}
                aria-roledescription={`Subscribe to fund (currently ${
                    subscribeActive ? "subscribed" : "unsubscribed"
                })`}
                onClick={handleClick(onClickSubscribe)}
            >
                <div
                    style={
                        {
                            "--animate-jiggle-delay": `-${jiggleDelay}s`,
                        } as CSSProperties
                    }
                    className={cx(
                        "delay-[--animate-jiggle-delay]",
                        subscribeJiggle && "animate-jiggle",
                        hasUpdates && redDot,
                    )}
                >
                    <SubscribeIcon active={subscribeActive} />
                </div>
            </button>
        </>
    );
};

export default SubscriptionButtons;

export const WatchIcon = ({
    className,
    active,
    ...props
}: React.SVGProps<SVGSVGElement> & { active?: boolean }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        strokeWidth="2"
        strokeLinecap="square"
        className={cx(
            "stroke-taupe-grey h-5 w-5 transition-colors",
            active ? "fill-core-yellow" : "fill-none",
            className,
        )}
        {...props}
    >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

export const SubscribeIcon = ({
    className,
    active,
    ...props
}: React.SVGProps<SVGSVGElement> & { active?: boolean }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cx(
            "stroke-taupe-grey h-5 w-5 transition-colors",
            active ? "fill-core-yellow" : "fill-none",
            className,
        )}
        {...props}
    >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
);
